/// <reference types="vite/client" />
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import { alpha, Theme } from '@mui/material/styles';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { StatusTooltip } from './StatusTooltip';

export type RemarkLevel = 'error' | 'warning';

export interface RemarkItem {
  level: RemarkLevel;
  message: string;
  /**
   * Usually sentences; the backend is moving to objects with named fields, so
   * anything is accepted and shown as best it can be.
   */
  details?: unknown[];
}

/** A detail as one line of text, whatever shape it arrived in. */
export const remarkDetailText = (detail: unknown): string => {
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'number' || typeof detail === 'boolean') {
    return String(detail);
  }
  if (detail && typeof detail === 'object') {
    const record = detail as Record<string, unknown>;
    const issue = record.issue_label ?? record.issue ?? record.message;
    const contract = record.contract_title;
    if (typeof issue === 'string') {
      return typeof contract === 'string' ? `${contract} - ${issue}` : issue;
    }
    return JSON.stringify(detail);
  }
  return '';
};

/**
 * How many issues the remarks carry: each detail is one, and a remark with no
 * details is one on its own - a folder's "Standard contract is missing", say.
 */
export const countRemarkIssues = (
  remark: Pick<RemarkItem, 'details'>[]
): number =>
  remark.reduce((total, r) => total + Math.max(r.details?.length ?? 0, 1), 0);

/** The tree row whose remark dot was clicked. */
export interface RemarkSource {
  /**
   * The file's or folder's own id, as the API knows it - a number, or
   * `contract:<uuid>` for a contract folder in the graph view. Not the tree
   * node id, which is a path built for the tree.
   */
  id?: number | string;
  name: string;
  isFolder: boolean;
}

export type RemarkSelection = {
  open: boolean;
  remark: RemarkItem[];
  source: RemarkSource | null;
  /** Just below the clicked dot, for a panel that opens beside it. */
  anchorPosition: { top: number; left: number } | null;
  /**
   * How the dot was used. A panel opened from the keyboard hands focus back
   * to the dot when it closes, so the reader keeps their place; one opened
   * with the pointer does not, or the returning focus would draw the dot's
   * focus ring and pop its tooltip over a panel that is still closing.
   */
  openedWith: 'pointer' | 'keyboard';
};

/**
 * Which row's remarks are open. A module store rather than a prop so a dot
 * deep in the tree can open a panel rendered once at page level, and so the lib
 * only says *which* row was clicked — the page decides what to show for it.
 */
const createRemarkSelectionStore = () => {
  let state: RemarkSelection = {
    open: false,
    remark: [],
    source: null,
    anchorPosition: null,
    openedWith: 'pointer',
  };
  /** Panels mounted to show a selection; with none, a dot has nothing to open. */
  let hosts = 0;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((fn) => fn());

  return {
    getState: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    open: (
      remark: RemarkItem[],
      source: RemarkSource,
      anchorEl?: HTMLElement | null,
      openedWith: RemarkSelection['openedWith'] = 'pointer'
    ) => {
      const rect = anchorEl?.getBoundingClientRect();
      state = {
        open: true,
        remark,
        source,
        anchorPosition: rect
          ? { top: rect.bottom + 4, left: rect.left + rect.width / 2 }
          : null,
        openedWith,
      };
      emit();
    },
    // Keep the remark and source while the drawer animates out, so it doesn't
    // empty itself mid-transition.
    close: () => {
      state = { ...state, open: false };
      emit();
    },
    hasHost: () => hosts > 0,
    registerHost: () => {
      hosts += 1;
      emit();
      return () => {
        hosts -= 1;
        // The last panel going takes any open selection with it.
        if (hosts === 0) state = { ...state, open: false };
        emit();
      };
    },
  };
};

type RemarkSelectionStore = ReturnType<typeof createRemarkSelectionStore>;

/**
 * One store for the page's life. A hot update re-runs this module and, without
 * this, makes a second store: the tree's dots would read the new one, which no
 * panel has registered with, and stop being clickable until a reload.
 */
// Vitest defines `import.meta.hot` without `data`.
const hotData = import.meta.hot?.data as
  | { remarkSelectionStore?: RemarkSelectionStore }
  | undefined;
const remarkSelectionStore: RemarkSelectionStore =
  hotData?.remarkSelectionStore ?? createRemarkSelectionStore();
if (hotData) hotData.remarkSelectionStore = remarkSelectionStore;

export const openRemarkSelection = remarkSelectionStore.open;
export const closeRemarkSelection = remarkSelectionStore.close;

/**
 * Marks the calling panel as the one that shows remark selections, for as
 * long as it is mounted. A dot only offers a click while some panel is: the
 * page decides whether there is one - behind a feature flag, say - and the
 * tree needs telling nothing.
 */
export function useRemarkSelectionHost(): void {
  React.useEffect(() => remarkSelectionStore.registerHost(), []);
}

/** The row whose remark dot was last clicked, and whether its panel is open. */
export function useRemarkSelection(): RemarkSelection {
  return React.useSyncExternalStore(
    remarkSelectionStore.subscribe,
    remarkSelectionStore.getState
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

export const LevelIcon = ({
  level,
  size = 18,
}: {
  level: RemarkLevel;
  size?: number;
}) =>
  level === 'error' ? (
    <ErrorIcon sx={{ fontSize: size, flexShrink: 0, color: `${level}.main` }} />
  ) : (
    <WarningAmberIcon
      sx={{ fontSize: size, flexShrink: 0, color: `${level}.main` }}
    />
  );

const RemarkDetailRow = ({
  detail,
  level,
}: {
  detail: string;
  level: RemarkLevel;
}) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: 1,
      bgcolor: alpha(theme.palette[level].main, 0.06),
      borderLeft: `3px solid ${theme.palette[level].main}`,
      fontSize: '0.8rem',
      lineHeight: 1.45,
      color: 'text.secondary',
    })}
  >
    {detail}
  </Box>
);

export const RemarkCard = ({ remark }: { remark: RemarkItem }) => {
  const level: RemarkLevel = remark.level === 'error' ? 'error' : 'warning';

  return (
    <Box
      sx={(theme: Theme) => ({
        borderRadius: 1.5,
        border: `1px solid ${alpha(theme.palette[level].main, 0.3)}`,
        bgcolor: alpha(theme.palette[level].main, 0.04),
        overflow: 'hidden',
      })}
    >
      <Box
        sx={(theme: Theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: alpha(theme.palette[level].main, 0.08),
          borderBottom: remark.details?.length
            ? `1px solid ${alpha(theme.palette[level].main, 0.15)}`
            : 'none',
        })}
      >
        <LevelIcon level={level} />
        <Box
          component="span"
          sx={{
            fontWeight: 700,
            fontSize: '0.85rem',
            lineHeight: 1.4,
            color: `${level}.dark`,
          }}
        >
          {remark.message}
        </Box>
      </Box>

      {remark.details && remark.details.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            p: 1.5,
          }}
        >
          {remark.details.map((detail, dIdx) => (
            <RemarkDetailRow
              key={dIdx}
              detail={remarkDetailText(detail)}
              level={level}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// RemarkPopoverHost — render once, outside the tree
// ---------------------------------------------------------------------------

/**
 * The remarks of the clicked row, in a popover beside its dot. The panel the
 * dots open when the page offers nothing richer; render it once, outside the
 * tree, and inside a Material theme.
 */
export const RemarkPopoverHost = () => {
  useRemarkSelectionHost();
  const { open, remark, anchorPosition, openedWith } = useRemarkSelection();

  if (!remark.length) return null;

  return (
    <Popover
      open={open}
      disableRestoreFocus={openedWith === 'pointer'}
      onClose={() => remarkSelectionStore.close()}
      onClick={(e) => e.stopPropagation()}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition ?? undefined}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            borderRadius: 2,
            maxWidth: 480,
            maxHeight: 400,
            overflow: 'auto',
            boxShadow: (theme: Theme) =>
              `0 8px 32px ${alpha(theme.palette.common.black, 0.18)}`,
          },
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {remark.map((r, index) => (
          <RemarkCard key={index} remark={r} />
        ))}
      </Box>
    </Popover>
  );
};

export type RemarkIndicatorProps = {
  remark: RemarkItem[];
  /** The row the dot belongs to, handed to whoever renders the remarks panel. */
  source: RemarkSource;
  /** Accessible name for the dot, e.g. "View missing clauses". */
  label?: string;
};

export const RemarkIndicator = ({
  remark,
  source,
  label,
}: RemarkIndicatorProps) => {
  const dotRef = React.useRef<HTMLSpanElement>(null);
  const isError = remark.some((r) => r.level === 'error');
  const interactive = React.useSyncExternalStore(
    remarkSelectionStore.subscribe,
    remarkSelectionStore.hasHost
  );

  // Pointer and key events stop here so opening the panel doesn't also select,
  // expand or drag the tree row.
  const open = (
    event: React.SyntheticEvent,
    openedWith: RemarkSelection['openedWith']
  ) => {
    event.stopPropagation();
    event.preventDefault();
    remarkSelectionStore.open(remark, source, dotRef.current, openedWith);
  };

  const { t } = useTranslation();
  // The count, not the messages: the panel the dot opens has those.
  const tooltipContent = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <LevelIcon level={isError ? 'error' : 'warning'} size={14} />
      <span>{t('{{count}} issues', { count: countRemarkIssues(remark) })}</span>
    </Box>
  );

  return (
    <Box
      sx={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center' }}
    >
      <StatusTooltip title={tooltipContent}>
        <Box
          component="span"
          ref={dotRef}
          {...(interactive
            ? {
                role: 'button',
                tabIndex: 0,
                'aria-label': label,
                onClick: (e: React.MouseEvent) => open(e, 'pointer'),
                onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') open(e, 'keyboard');
                },
              }
            : {})}
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: isError ? 'error.main' : 'warning.main',
            display: 'inline-block',
            marginInlineStart: '6px',
            // Only a pointer when there is a panel for the click to open.
            cursor: interactive ? 'pointer' : 'default',
            flexShrink: 0,
            '&:focus-visible': {
              outline: '2px solid',
              // A token both Material and Joy themes carry: the dot renders
              // under either, and an unknown one falls back to black.
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        />
      </StatusTooltip>
    </Box>
  );
};
